import { ResultsDisplay } from "./PlatoResultsContainer/PlatoResultsContainer";
import { useState, useMemo, useEffect, useRef } from "react";
import { 
  Brain, 
  Activity, 
  MapPin, 
  BarChart3, 
  CheckCircle, 
  Clock, 
  Calculator, 
  X, 
  CheckSquare, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown, 
  Megaphone,
  Users,
  Move,
  Filter,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  ListFilter,
  CalendarDays,
  Trash2,
  AlertCircle 
} from "lucide-react";
import { auth } from "../../firebase";
import { logCalls } from "../../logging";

// ====================================================================================
// LOGIC EXTRACTION: Strategic Alignment Matrix (Synced with plato_engine.py)
// ====================================================================================
const STRATEGIC_ALIGNMENT_MATRIX = {
    'GAIN_PRIORITY': ["swing_voter_conversion", "late_decider_capture"],
    'GAIN_TARGET': ["swing_voter_conversion", "issue_based_persuasion"],
    'DEFEND_CRITICAL': ["core_support_mobilization", "defensive_retention", "voter_turnout"],
    'DEFEND_URGENT': ["core_support_mobilization", "defensive_retention"],
    'SAFE_HOLD': ["voter_turnout", "youth_engagement"],
    'UNWINNABLE': ["issue_based_persuasion"]
};

// --- AESTHETIC HELPERS ---
const GlassContainer = ({ children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl ${className}`}>
        {children}
    </div>
);

// ====================================================================================
// INTERNAL COMPONENT: Draggable Mini Calculator
// ====================================================================================
const MiniCalculator = ({ onClose }) => {
    const [display, setDisplay] = useState("");
    
    // Dragging State
    const [position, setPosition] = useState(() => {
        if (typeof window !== 'undefined') {
            return { x: window.innerWidth - 350, y: 180 };
        }
        return { x: 800, y: 150 };
    });
    
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const nodeRef = useRef(null);

    const handleMouseDown = (e) => {
        if (nodeRef.current) {
            setIsDragging(true);
            const rect = nodeRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const newX = e.clientX - dragOffset.x;
                const newY = e.clientY - dragOffset.y;
                setPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const handleClick = (value) => {
        if (value === "=") {
            try {
                // eslint-disable-next-line no-eval
                setDisplay(String(eval(display))); 
            } catch (e) {
                setDisplay("Error");
            }
        } else if (value === "C") {
            setDisplay("");
        } else {
            setDisplay((prev) => prev + value);
        }
    };

    const buttons = [
        "7", "8", "9", "/",
        "4", "5", "6", "*",
        "1", "2", "3", "-",
        "C", "0", "=", "+"
    ];

    return (
        <div 
            ref={nodeRef}
            style={{ 
                left: `${position.x}px`, 
                top: `${position.y}px`,
                position: 'fixed' 
            }}
            className="z-50 w-64 bg-slate-800 border border-slate-600 rounded-lg shadow-2xl shadow-black p-4 animate-in fade-in zoom-in duration-75"
        >
            <div 
                onMouseDown={handleMouseDown}
                className="flex justify-between items-center mb-3 border-b border-slate-700 pb-2 cursor-move group"
            >
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2 group-hover:text-white transition-colors">
                    <Calculator size={14} /> Quick Calc <Move size={10} className="ml-1 opacity-50" />
                </span>
                <button 
                    onClick={(e) => { e.stopPropagation(); onClose(); }} 
                    className="text-slate-500 hover:text-white transition-colors cursor-pointer"
                >
                    <X size={16} />
                </button>
            </div>

            <div className="bg-slate-900 rounded p-2 mb-3 text-right text-white font-mono text-lg h-10 overflow-hidden truncate">
                {display || "0"}
            </div>
            <div className="grid grid-cols-4 gap-2">
                {buttons.map((btn) => (
                    <button
                        key={btn}
                        onClick={() => handleClick(btn)}
                        className={`h-10 rounded text-sm font-bold transition-all ${
                            btn === "=" 
                            ? "bg-accent-600 hover:bg-accent-500 text-white" 
                            : btn === "C"
                            ? "bg-red-900/50 hover:bg-red-800/50 text-red-200"
                            : ["/", "*", "-", "+"].includes(btn)
                            ? "bg-slate-700 hover:bg-slate-600 text-accent-300"
                            : "bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700"
                        }`}
                    >
                        {btn}
                    </button>
                ))}
            </div>
        </div>
    );
};

// ====================================================================================
// INTERNAL COMPONENT: Click-Outside Aware Mini Calendar
// ====================================================================================
const MiniCalendar = ({ onClose, onDateSelect }) => {
    const [viewDate, setViewDate] = useState(new Date());
    const calendarRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getFirstDayOfMonth(year, month);
    
    const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

    const handleDayClick = (day) => {
        const selectedDate = new Date(year, month, day);
        selectedDate.setHours(0, 0, 0, 0); 
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const diffTime = selectedDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        onDateSelect(diffDays > 0 ? diffDays : 0);
        onClose();
    };

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const empties = Array.from({ length: startDay }, (_, i) => i);

    return (
        <div ref={calendarRef} className="absolute top-12 right-0 z-50 w-64 bg-slate-800 border border-slate-600 rounded-lg shadow-2xl shadow-black p-4 animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-4 border-b border-slate-700 pb-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <Calendar size={14} /> Election Date
                </span>
                <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                    <X size={16} />
                </button>
            </div>

            <div className="flex justify-between items-center mb-3 bg-slate-900/50 p-1 rounded">
                <button onClick={handlePrevMonth} className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                    <ChevronLeft size={16} />
                </button>
                <span className="text-sm font-bold text-white">
                    {monthNames[month]} {year}
                </span>
                <button onClick={handleNextMonth} className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                    <ChevronRight size={16} />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2 text-center">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                    <span key={`${d}-${i}`} className="text-[10px] text-slate-500 font-bold">{d}</span>
                ))}
                {empties.map((_, i) => <div key={`empty-${i}`} />)}
                {days.map(d => {
                    const checkDate = new Date(year, month, d);
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    const isPast = checkDate < today;

                    return (
                        <button
                            key={d}
                            onClick={() => !isPast && handleDayClick(d)}
                            disabled={isPast}
                            className={`h-7 w-7 text-xs rounded flex items-center justify-center transition-all ${
                                isPast 
                                ? "text-slate-600 cursor-not-allowed" 
                                : "text-slate-200 hover:bg-accent-600 hover:text-white hover:scale-110 bg-slate-700/50"
                            }`}
                        >
                            {d}
                        </button>
                    )
                })}
            </div>
            
            <p className="text-[10px] text-slate-500 text-center mt-2">
                Calculates days remaining.
            </p>
        </div>
    );
};

// ====================================================================================
// NEW COMPONENT: Enhanced Date Range Picker
// ====================================================================================
const EnhancedDateRangePicker = ({ onClose, onRangeSelect, initialStart, initialEnd }) => {
    const getMidnight = (dStr) => {
        const d = dStr ? new Date(dStr) : new Date();
        d.setHours(0,0,0,0);
        return d;
    };

    const [viewDate, setViewDate] = useState(getMidnight(initialStart || new Date()));
    const [tempStart, setTempStart] = useState(initialStart ? getMidnight(initialStart) : null);
    const [tempEnd, setTempEnd] = useState(initialEnd ? getMidnight(initialEnd) : null);
    
    const calendarRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getFirstDayOfMonth(year, month);
    
    const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

    const handleDayClick = (day) => {
        const selectedDate = new Date(year, month, day);
        selectedDate.setHours(0, 0, 0, 0);

        if (!tempStart || (tempStart && tempEnd)) {
            setTempStart(selectedDate);
            setTempEnd(null);
            onRangeSelect(selectedDate, null); 
        } 
        else if (tempStart && !tempEnd) {
            if (selectedDate < tempStart) {
                setTempStart(selectedDate);
                onRangeSelect(selectedDate, null);
            } else {
                setTempEnd(selectedDate);
                onRangeSelect(tempStart, selectedDate);
            }
        }
    };

    const applyPreset = (type) => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const currentYear = today.getFullYear();

        let start = new Date(today);
        let end = new Date(today);
        let targetYear = currentYear;

        const getSmartQuarterYear = (endMonth, endDay) => {
            const qEnd = new Date(currentYear, endMonth, endDay);
            return qEnd < today ? currentYear + 1 : currentYear;
        };

        switch(type) {
            case '1_day': break;
            case 'weekend':
                const day = today.getDay(); 
                const diffToFriday = 5 - day;
                if (diffToFriday < 0) {
                    start.setDate(today.getDate() + (diffToFriday + 7));
                } else {
                    start.setDate(today.getDate() + diffToFriday);
                }
                end = new Date(start);
                end.setDate(start.getDate() + 2); 
                break;
            case '1_week':
                end.setDate(today.getDate() + 7);
                break;
            case '1_month':
                end.setMonth(today.getMonth() + 1);
                break;
            case 'q1': 
                targetYear = getSmartQuarterYear(2, 31);
                start = new Date(targetYear, 0, 1);
                end = new Date(targetYear, 2, 31);
                break;
            case 'q2': 
                targetYear = getSmartQuarterYear(5, 30);
                start = new Date(targetYear, 3, 1);
                end = new Date(targetYear, 5, 30);
                break;
            case 'q3': 
                targetYear = getSmartQuarterYear(8, 30);
                start = new Date(targetYear, 6, 1);
                end = new Date(targetYear, 8, 30);
                break;
            case 'q4': 
                targetYear = getSmartQuarterYear(11, 31);
                start = new Date(targetYear, 9, 1);
                end = new Date(targetYear, 11, 31);
                break;
            default: break;
        }

        setTempStart(start);
        setTempEnd(end);
        setViewDate(start);
        onRangeSelect(start, end);
    };

    const isStart = (d) => {
        if (!tempStart) return false;
        const check = new Date(year, month, d);
        check.setHours(0,0,0,0);
        return check.getTime() === tempStart.getTime();
    };

    const isEnd = (d) => {
        if (!tempEnd) return false;
        const check = new Date(year, month, d);
        check.setHours(0,0,0,0);
        return check.getTime() === tempEnd.getTime();
    };

    const isInRange = (d) => {
        if (!tempStart || !tempEnd) return false;
        const check = new Date(year, month, d);
        check.setHours(0,0,0,0);
        return check > tempStart && check < tempEnd;
    };

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const daysArr = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const empties = Array.from({ length: startDay }, (_, i) => i);

    return (
        <div ref={calendarRef} className="absolute top-12 left-0 z-50 flex flex-row bg-slate-800 border border-slate-600 rounded-lg shadow-2xl shadow-black animate-in fade-in zoom-in duration-200 overflow-hidden">
            
            <div className="w-32 bg-slate-900/50 border-r border-slate-700 p-2 flex flex-col gap-1">
                <span className="text-[10px] uppercase font-bold text-slate-500 mb-1 pl-1">Quick Select</span>
                {[
                    { label: "1 Day", val: '1_day' },
                    { label: "This Weekend", val: 'weekend' },
                    { label: "1 Week", val: '1_week' },
                    { label: "1 Month", val: '1_month' },
                ].map(p => (
                    <button key={p.val} onClick={() => applyPreset(p.val)} className="text-xs text-left px-2 py-1.5 rounded text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
                        {p.label}
                    </button>
                ))}
                
                <div className="my-1 border-t border-slate-700/50"></div>
                <span className="text-[10px] uppercase font-bold text-slate-500 mb-1 pl-1">Fiscal</span>
                {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                    <button key={q} onClick={() => applyPreset(q.toLowerCase())} className="text-xs text-left px-2 py-1.5 rounded text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
                        {q}
                    </button>
                ))}
            </div>

            <div className="p-4 w-64">
                <div className="flex justify-between items-center mb-4 border-b border-slate-700 pb-2">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <CalendarDays size={14} /> Campaign Dates
                    </span>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                        <X size={16} />
                    </button>
                </div>

                <div className="flex justify-between items-center mb-3 bg-slate-900/50 p-1 rounded">
                    <button onClick={handlePrevMonth} className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                        <ChevronLeft size={16} />
                    </button>
                    <span className="text-sm font-bold text-white">
                        {monthNames[month]} {year}
                    </span>
                    <button onClick={handleNextMonth} className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                        <ChevronRight size={16} />
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-y-1 gap-x-0 mb-2 text-center">
                    {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                        <span key={`${d}-${i}`} className="text-[10px] text-slate-500 font-bold">{d}</span>
                    ))}
                    {empties.map((_, i) => <div key={`empty-${i}`} />)}
                    {daysArr.map(d => {
                        const checkDate = new Date(year, month, d);
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const isPast = checkDate < today;

                        const start = !isPast && isStart(d);
                        const end = !isPast && isEnd(d);
                        const range = !isPast && isInRange(d);
                        
                        let baseClasses = "h-7 w-full text-xs flex items-center justify-center transition-all relative";
                        let styleClasses = "text-slate-300 hover:bg-slate-700 hover:text-white rounded";

                        if (isPast) {
                            styleClasses = "text-slate-600 cursor-not-allowed rounded";
                        } else if (start) {
                            styleClasses = "bg-accent-600 text-white font-bold shadow-md z-10 rounded-l-lg rounded-r-none";
                            if (!tempEnd || (tempEnd && tempStart && tempEnd.getTime() === tempStart.getTime())) {
                                styleClasses = "bg-accent-600 text-white font-bold shadow-md z-10 rounded-lg";
                            }
                        } else if (end) {
                            styleClasses = "bg-accent-600 text-white font-bold shadow-md z-10 rounded-r-lg rounded-l-none";
                        } else if (range) {
                            styleClasses = "bg-accent-600/30 text-white rounded-none font-semibold z-0";
                        }

                        return (
                            <button
                                key={d}
                                onClick={() => !isPast && handleDayClick(d)}
                                disabled={isPast}
                                className={`${baseClasses} ${styleClasses}`}
                            >
                                {d}
                            </button>
                        )
                    })}
                </div>
                
                <div className="flex justify-end mt-4">
                    <button 
                        onClick={onClose}
                        className="text-[10px] bg-accent-600 hover:bg-accent-500 text-white px-3 py-1 rounded transition-colors font-semibold"
                    >
                        Apply Dates
                    </button>
                </div>
            </div>
        </div>
    );
};

// ====================================================================================
// INTERNAL COMPONENT: Channel Card
// ====================================================================================
const ChannelCard = ({ 
    channel, 
    type, 
    campaignInput, 
    onToggle, 
    onCostChange, 
    onHoursChange 
}) => {
    const isSelected = campaignInput.selected_channels.includes(channel.channel);
    const hasCost = !!channel.costLabel;
    const hasHours = !!channel.hoursLabel;

    // --- Aesthetic Updates for Cards (Reverted to colored glow for better visibility) ---
    const activeClass = type === 'field' 
        ? 'bg-blue-950/40 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.15)]' 
        : 'bg-emerald-950/40 border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.15)]';
        
    const hoverClass = type === 'field'
        ? 'hover:border-blue-400/30 hover:bg-blue-900/20'
        : 'hover:border-emerald-400/30 hover:bg-emerald-900/20';

    const checkboxColor = type === 'field' ? 'bg-blue-600 border-blue-500' : 'bg-emerald-600 border-emerald-500';
    const focusRing = type === 'field' ? 'focus:border-blue-500 focus:ring-blue-500/30' : 'focus:border-emerald-500 focus:ring-emerald-500/30';

    return (
        <div 
            className={`p-3 rounded-lg border transition-all duration-200 flex flex-col justify-between group relative ${
                isSelected 
                ? `${activeClass} shadow-lg` 
                : `bg-slate-900/30 border-slate-800/50 opacity-80 hover:opacity-100 ${hoverClass}`
            }`}
        >
            {/* Header */}
            <div className="flex items-start justify-between mb-3 relative z-10">
                <label className="flex items-center space-x-3 cursor-pointer select-none w-full">
                    <div className={`relative flex items-center justify-center w-5 h-5 rounded border transition-all duration-200 ${
                        isSelected 
                        ? `${checkboxColor} scale-105` 
                        : 'bg-slate-950/50 border-slate-700 group-hover:border-slate-500'
                    }`}>
                        <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => onToggle(channel.channel)}
                            className="absolute opacity-0 w-full h-full cursor-pointer"
                        />
                        {isSelected && <CheckCircle size={14} className="text-white" />}
                    </div>
                    <span className={`font-medium text-sm leading-tight transition-colors ${isSelected ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}>
                        {channel.label}
                    </span>
                </label>
            </div>

            {/* Inputs */}
            <div className="space-y-2 relative z-10">
                {hasCost && (
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className={`text-[10px] uppercase tracking-wider font-bold ${isSelected ? 'text-slate-300' : 'text-slate-600'}`}>
                                {channel.costLabel}
                            </label>
                        </div>
                        <div className="relative group/input">
                            <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 font-sans text-xs ${isSelected ? 'text-slate-400' : 'text-slate-700'}`}>£</span>
                            <input 
                                type="number" 
                                step="0.01" 
                                min="0"
                                disabled={!isSelected}
                                value={campaignInput.channel_costs[channel.channel] ?? 0} 
                                onChange={e => onCostChange(channel.channel, e.target.value)} 
                                placeholder="0.00"
                                className={`pl-6 form-text w-full py-1.5 text-xs rounded transition-all ${
                                    !isSelected 
                                    ? 'cursor-not-allowed text-slate-600 bg-slate-950/30 border-slate-800/50' 
                                    : `bg-slate-950/80 border-slate-600 ${focusRing} text-white shadow-inner`
                                }`}
                            />
                        </div>
                    </div>
                )}
                {hasHours && (
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className={`text-[10px] uppercase tracking-wider font-bold ${isSelected ? 'text-slate-300' : 'text-slate-600'}`}>
                                {channel.hoursLabel}
                            </label>
                        </div>
                        <div className="relative group/input">
                            <Clock size={12} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isSelected ? 'text-slate-400' : 'text-slate-700'}`} />
                            <input 
                                type="number" 
                                step="1" 
                                min="0"
                                disabled={!isSelected}
                                value={campaignInput.channel_hours[channel.channel] ?? 0} 
                                onChange={e => onHoursChange(channel.channel, e.target.value)} 
                                placeholder="Hrs"
                                className={`pl-7 form-text w-full py-1.5 text-xs rounded transition-all ${
                                    !isSelected 
                                    ? 'cursor-not-allowed text-slate-600 bg-slate-950/30 border-slate-800/50' 
                                    : `bg-slate-950/80 border-slate-600 ${focusRing} text-white shadow-inner`
                                }`}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ====================================================================================
// API Logic
// ====================================================================================



export const runOptimization = async ({ setIsLoading, setResults, setOpenChannel, campaignInput, token, campaignID }) => {
    setIsLoading(true);
    try {
        console.hidden("🚀 Submitting Optimization Payload:", campaignInput);

        const response = await fetch("https://plato-campaign-analytics-84264019333.europe-west1.run.app", {
            method: "POST",
            body: JSON.stringify(campaignInput), 
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            mode: "cors"
        });

        const rawText = await response.text();
        const sanitizedText = rawText
            .replace(/:\s*Infinity/g, ': null')
            .replace(/:\s*-Infinity/g, ': null')
            .replace(/:\s*NaN/g, ': null');

        const results = JSON.parse(sanitizedText);

        setResults(results);
        setIsLoading(false);
        setOpenChannel(null);
    } catch (error) {
        console.error("Optimization failed:", error);
        alert("Optimization Error: The calculation resulted in an invalid value (likely due to 0 votes/budget). Please check your inputs.");
        setIsLoading(false);
    }
};

// ====================================================================================
// Component: PlatoView
// ====================================================================================

const campaignFocusOptions = [
    { value: "voter_turnout", label: "Voter Turnout Drive", description: "Mobilize existing supporters to get to the polls." },
    { value: "swing_voter_conversion", label: "Swing Voter Conversion", description: "Persuade undecided voters in marginal seats." },
    { value: "core_support_mobilization", label: "Core Support Mobilization", description: "Energize and activate your loyal base." },
    { value: "youth_engagement", label: "Youth Engagement", description: "Target demographics under 30 via specific channels." },
    { value: "defensive_retention", label: "Defensive Retention", description: "Protect vulnerable seats from opposition targeting." },
    { value: "tactical_voting", label: "Tactical Voting", description: "Encourage strategic voting to block opposition." },
    { value: "issue_based_persuasion", label: "Issue-Based Persuasion", description: "Focus campaign messaging on specific local issues." },
    { value: "late_decider_capture", label: "Late Decider Capture", description: "High-intensity blitz for last-minute deciders." },
];

const partyOptions = [
    { value: "conservative", label: "Conservative" },
    { value: "labour", label: "Labour" },
    { value: "liberal_democrat", label: "Liberal Democrat" },
    { value: "green", label: "Green" },
    { value: "reform", label: "Reform" },
    { value: "snp", label: "SNP" },
    { value: "plaid_cymru", label: "Plaid Cymru" },
];

const channelData = [
    { channel: "canvass_targeted", label: "Targeted Canvassing (V7)", hoursLabel: "Vol. Hours" },
    { channel: "leaflet_universal_vol", label: "Volunteer Leaflets (V7)", costLabel: "Print Cost", hoursLabel: "Del. Time" },
    { channel: "field_stakeboards", label: "Field Stakeboards (V7)", costLabel: "Board Cost", hoursLabel: "Install Hrs" },
    { channel: "door_to_door", label: "Door-to-Door (Legacy)", costLabel: "Cost/Contact", hoursLabel: "Vol. Hours" },
    { channel: "phone_banking", label: "Phone Banking", costLabel: "Call Cost", hoursLabel: "Vol. Hours" },
    { channel: "community_events", label: "Community Events", costLabel: "Event Budget", hoursLabel: "Staff Hours" },
    { channel: "leaflet_universal_paid", label: "Paid Leaflets (Royal Mail)", costLabel: "Print & Post" },
    { channel: "direct_mail_targeted", label: "Targeted Direct Mail", costLabel: "Total Cost" },
    { channel: "direct_mail_social_pressure", label: "GOTV 'Social Pressure' Mail", costLabel: "Total Cost" },
    { channel: "postal_leaflet", label: "Postal Leaflets (Legacy)", costLabel: "Total Cost" },
    { channel: "digital_ads", label: "Digital Advertising", costLabel: "Ad Budget" },
    { channel: "social_media", label: "Social Media (Boost/Ads)", costLabel: "Budget" },
    { channel: "text_message", label: "SMS Broadcast", costLabel: "Msg Cost" },
    { channel: "email_campaign", label: "Email Campaign", costLabel: "Platform Cost" },
    { channel: "radio_ads", label: "Radio Advertising", costLabel: "Media Buy" },
    { channel: "newspaper_advertising", label: "Newspaper Advertising", costLabel: "Media Buy" }
];

export const PlatoView = ({ wards, swingSeats, campaignId, setActiveView }) => {
const {logMatrixCall} = logCalls(campaignId);

    const allWards = Object.values(wards ?? []).flatMap(region =>
        Object.values(region).flatMap(authority =>
            Object.values(authority).map(ward => ({ id: ward.ward_code, name: ward.ward_name }))
        )
    ).sort((a, b) => a.name.localeCompare(b.name));

    const [campaignInput, setCampaignInput] = useState({
        campaign_name: 'New Campaign',
        campaign_focus: campaignFocusOptions[1].value,
        budget: 5000.0,
        days_until_election: 42,
        execution_start_date: null,
        execution_end_date: null,
        party: partyOptions[2].value,
        selected_ward_ids: [],
        selected_channels: [],
        channel_costs: {
            "canvass_targeted": 0.00, "leaflet_universal_vol": 0.05, "field_stakeboards": 6.50,
            "door_to_door": 0.00, "phone_banking": 0.10, "community_events": 150.00,
            "leaflet_universal_paid": 0.65, "direct_mail_targeted": 0.55,
            "direct_mail_social_pressure": 0.45, "postal_leaflet": 0.85, "digital_ads": 100,
            "social_media": 100.00, "text_message": 0.05, "email_campaign": 0.02,
            "radio_ads": 85.00, "newspaper_advertising": 45.00
        },
        channel_hours: {
            "canvass_targeted": 100, "leaflet_universal_vol": 50, "field_stakeboards": 10,
            "door_to_door": 100, "phone_banking": 20, "community_events": 10
        }
    });

    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [openChannel, setOpenChannel] = useState(null);
    const [showCalculator, setShowCalculator] = useState(false);
    const [showDurationCalendar, setShowDurationCalendar] = useState(false);
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [showFocusDropdown, setShowFocusDropdown] = useState(false);
    const focusDropdownRef = useRef(null);
    const [swingSort, setSwingSort] = useState('desc'); 
    const [filterStrategy, setFilterStrategy] = useState(false); 

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (focusDropdownRef.current && !focusDropdownRef.current.contains(event.target)) {
                setShowFocusDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const { fieldOpsChannels, paidMediaChannels } = useMemo(() => {
        const field = [];
        const media = [];
        channelData.forEach(c => {
            if (c.hoursLabel) field.push(c);
            else media.push(c);
        });
        return { fieldOpsChannels: field, paidMediaChannels: media };
    }, []);

    const processedSwingSeats = useMemo(() => {
        if (!swingSeats || !Array.isArray(swingSeats)) return [];
        let processed = [...swingSeats];

        if (filterStrategy) {
            const userFocus = campaignInput.campaign_focus;
            const validStrategyKeys = Object.entries(STRATEGIC_ALIGNMENT_MATRIX)
                .filter(([_, focusList]) => focusList.includes(userFocus))
                .map(([key]) => key); 

            processed = processed.filter(seat => {
                const rawStrategy = seat.strategic_priority?.campaign_strategy || "";
                const normalizedSeatStrategy = rawStrategy.toString()
                    .toUpperCase()
                    .replace(/[^A-Z0-9]+/g, '_') 
                    .replace(/^_+|_+$/g, '');   
                return validStrategyKeys.includes(normalizedSeatStrategy);
            });
        }

        processed.sort((a, b) => {
            const scoreA = parseFloat(a.winability?.score) || 0;
            const scoreB = parseFloat(b.winability?.score) || 0;
            return swingSort === 'desc' ? scoreB - scoreA : scoreA - scoreB;
        });

        return processed;
    }, [swingSeats, swingSort, filterStrategy, campaignInput.campaign_focus]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCampaignInput(prev => ({ ...prev, [name]: value }));
    };

    const handleBudgetChange = (e) => {
        const rawValue = e.target.value.replace(/,/g, '');
        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
            setCampaignInput(prev => ({ ...prev, budget: rawValue }));
        }
    };

    const formatBudgetDisplay = (val) => {
        if (!val) return "";
        const parts = val.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join('.');
    };
    
    const handleFocusSelect = (value) => {
        setCampaignInput(prev => ({ ...prev, campaign_focus: value }));
        setShowFocusDropdown(false);
    };

    const handleDurationDateSelection = (days) => {
        setCampaignInput(prev => ({ 
            ...prev, 
            days_until_election: days 
        }));
    };

    const handlePayloadDateSelection = (startDate, endDate) => {
        if (!startDate) return;
        const formatDateObj = (date) => date.toISOString().split('T')[0];
        
        setCampaignInput(prev => ({
            ...prev,
            execution_start_date: formatDateObj(startDate),
            execution_end_date: endDate ? formatDateObj(endDate) : formatDateObj(startDate)
        }));
    };

    const handleClearDates = (e) => {
        e.stopPropagation();
        setCampaignInput(prev => ({
            ...prev,
            execution_start_date: null,
            execution_end_date: null
        }));
    };

    const isDateRangeInvalid = useMemo(() => {
        const durationDays = parseInt(campaignInput.days_until_election || 0);
        if (campaignInput.execution_start_date) {
            const today = new Date();
            today.setHours(0,0,0,0);
            
            const deadlineDate = new Date(today);
            deadlineDate.setDate(today.getDate() + durationDays);

            const start = new Date(campaignInput.execution_start_date);
            if (start > deadlineDate) return true;

            if (campaignInput.execution_end_date) {
                const end = new Date(campaignInput.execution_end_date);
                if (end > deadlineDate) return true;
            }
        }
        return false;
    }, [campaignInput.execution_start_date, campaignInput.execution_end_date, campaignInput.days_until_election]);

    const handleChannelCostChange = (channel, value) => {
        setCampaignInput(prev => ({
            ...prev,
            channel_costs: { ...prev.channel_costs, [channel]: parseFloat(value) || 0 }
        }));
    };

    const handleChannelHoursChange = (channel, value) => {
        setCampaignInput(prev => ({
            ...prev,
            channel_hours: { ...prev.channel_hours, [channel]: parseFloat(value) || 0 }
        }));
    };

    const handleChannelToggle = (channelId) => {
        setCampaignInput(prev => {
            const isSelected = prev.selected_channels.includes(channelId);
            const newChannels = isSelected
                ? prev.selected_channels.filter(id => id !== channelId)
                : [...prev.selected_channels, channelId];
            return { ...prev, selected_channels: newChannels };
        });
    };

    const handleWardToggle = (wardId) => {
        setCampaignInput(prev => {
            const isSelected = prev.selected_ward_ids.includes(wardId);
            const newWards = isSelected
                ? prev.selected_ward_ids.filter(id => id !== wardId)
                : [...prev.selected_ward_ids, wardId];
            return { ...prev, selected_ward_ids: newWards };
        });
    };

    const handleClearWards = () => {
        setCampaignInput(prev => ({
            ...prev,
            selected_ward_ids: []
        }));
    };

    const handleGlobalSelection = () => {
        if (campaignInput.selected_channels.length >= 2) {
            setCampaignInput(prev => ({ ...prev, selected_channels: [] }));
        } else {
            setCampaignInput(prev => ({ 
                ...prev, 
                selected_channels: channelData.map(c => c.channel) 
            }));
        }
    };

    const handleRunOptimization = async () => {
        if (!auth.currentUser) {
            alert("Please sign in to run the optimization.");
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();
            const targetWards = campaignInput.selected_ward_ids.length > 0 
                ? campaignInput.selected_ward_ids 
                : allWards.map(w => w.id);

            const activeChannelCosts = Object.keys(campaignInput.channel_costs)
                .filter(key => campaignInput.selected_channels.includes(key))
                .reduce((obj, key) => {
                    obj[key] = campaignInput.channel_costs[key];
                    return obj;
                }, {});
            
            const activeChannelHours = Object.keys(campaignInput.channel_hours)
                .filter(key => campaignInput.selected_channels.includes(key))
                .reduce((obj, key) => {
                    obj[key] = campaignInput.channel_hours[key];
                    return obj;
                }, {});

            const apiPayload = {
                campaignID: campaignId,
                campaign_name: campaignInput.campaign_name,
                campaign_focus: campaignInput.campaign_focus,
                party: campaignInput.party,
                budget: parseFloat(campaignInput.budget) || 0,
                days_until_election: parseInt(campaignInput.days_until_election) || 30,
                execution_start_date: campaignInput.execution_start_date,
                execution_end_date: campaignInput.execution_end_date,
                selected_ward_ids: targetWards,
                channel_costs: activeChannelCosts,
                channel_hours: activeChannelHours
            };

            logMatrixCall();

            await runOptimization({
                setIsLoading,
                setResults,
                setOpenChannel,
                campaignInput: apiPayload,
                token: token,
                campaignID: campaignId
            });

        } catch (error) {
            console.error("Error preparing campaign data:", error);
            setIsLoading(false);
        }
    };

    const getWinabilityColorClass = (score) => {
        const numericScore = parseFloat(score);
        if (numericScore >= 0.7) return 'border-green-400 bg-green-900/30 hover:border-green-300';
        if (numericScore >= 0.6) return 'border-green-500 bg-green-900/20 hover:border-green-400';
        if (numericScore >= 0.5) return 'border-amber-400 bg-amber-900/30 hover:border-amber-300';
        if (numericScore >= 0.4) return 'border-amber-500 bg-amber-900/20 hover:border-amber-400';
        if (numericScore >= 0.3) return 'border-red-500 bg-red-900/20 hover:border-red-400';
        return 'border-red-600 bg-red-900/10 hover:border-red-500';
    };

    const focusLabel = campaignFocusOptions.find(opt => opt.value === campaignInput.campaign_focus)?.label || 'Unknown Focus';
    const effectiveWardCount = campaignInput.selected_ward_ids.length > 0 
        ? campaignInput.selected_ward_ids.length 
        : allWards.length;
    const channelCount = campaignInput.selected_channels.length;
    const formattedBudget = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(campaignInput.budget);
    const isMultiSelection = campaignInput.selected_channels.length >= 2;

    const dateRangeDisplay = !campaignInput.execution_start_date 
        ? "Select Dates" 
        : campaignInput.execution_start_date === campaignInput.execution_end_date
            ? campaignInput.execution_start_date
            : `${campaignInput.execution_start_date} → ${campaignInput.execution_end_date}`;

    return (
        <div className="p-8 space-y-8 animate-in fade-in duration-500">
            
            {/* 1. Left Panel: User Inputs (Dark Glass Reskin) */}
            <GlassContainer className="p-6 space-y-8 overflow-visible">
                <div className="flex items-center space-x-3 border-b border-slate-700/50 pb-4">
                    <Brain className="text-white" size={24} />
                    <h3 className="text-s font-bold text-slate-200 uppercase tracking-widest">Campaign Matrix</h3>
                </div>

                {/* Core Details Section */}
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2 block">Campaign Name</label>
                            <input 
                                type="text" 
                                name="campaign_name" 
                                value={campaignInput.campaign_name} 
                                onChange={handleInputChange} 
                                className="w-full bg-slate-950/50 border border-slate-700/60 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" 
                            />
                        </div>
                        
                        <div className="relative" ref={focusDropdownRef}>
                            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2 block">Campaign Focus</label>
                            <button
                                onClick={() => setShowFocusDropdown(!showFocusDropdown)}
                                className="w-full bg-slate-950/50 border border-slate-700/60 rounded-lg px-4 py-2.5 text-sm text-white text-left flex justify-between items-center hover:border-slate-500 transition-colors"
                            >
                                <span className="truncate">{focusLabel}</span>
                                <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${showFocusDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {showFocusDropdown && (
                                <div className="absolute z-50 w-full mt-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                    <div className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600">
                                        {campaignFocusOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => handleFocusSelect(option.value)}
                                                className={`w-full text-left p-3 hover:bg-slate-800 transition-colors border-l-4 ${
                                                    campaignInput.campaign_focus === option.value
                                                    ? "bg-slate-800 border-white"
                                                    : "border-transparent"
                                                }`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className={`text-sm font-bold ${
                                                            campaignInput.campaign_focus === option.value ? "text-white" : "text-slate-300"
                                                        }`}>
                                                            {option.label}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-0.5">
                                                            {option.description}
                                                        </div>
                                                    </div>
                                                    {campaignInput.campaign_focus === option.value && (
                                                        <CheckCircle size={14} className="text-white mt-0.5" />
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2 block">Budget (£)</label>
                            <input 
                                type="text" 
                                name="budget" 
                                value={formatBudgetDisplay(campaignInput.budget)} 
                                onChange={handleBudgetChange} 
                                className="w-full bg-slate-950/50 border border-slate-700/60 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" 
                                placeholder="0"
                            />
                        </div>
                        
                        <div className={`flex flex-col ${isDateRangeInvalid ? 'bg-red-950/20 border border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.3)] rounded-lg p-2 transition-all duration-300' : ''}`}>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative">
                                    <label className={`text-xs font-bold uppercase tracking-wider mb-2 block ${isDateRangeInvalid ? 'text-red-300' : 'text-slate-300'}`}>Target Dates</label>
                                    <div className="relative group/date-trigger">
                                        <button
                                            onClick={() => setShowRangePicker(!showRangePicker)}
                                            className={`w-full bg-slate-950/50 border border-slate-700/60 rounded-lg px-4 py-2.5 text-sm text-left flex justify-between items-center transition-colors ${
                                                campaignInput.execution_start_date ? "text-white" : "text-slate-500"
                                            } ${isDateRangeInvalid ? 'border-red-500 bg-red-900/20' : ''}`}
                                        >
                                            <span className="truncate">{dateRangeDisplay}</span>
                                            <CalendarDays size={14} className={`${isDateRangeInvalid ? 'text-red-400' : 'text-slate-400'} flex-shrink-0`} />
                                        </button>
                                        
                                        {campaignInput.execution_start_date && (
                                            <button
                                                onClick={handleClearDates}
                                                className="absolute right-10 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white transition-colors"
                                                title="Clear Dates"
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                    {showRangePicker && (
                                        <EnhancedDateRangePicker 
                                            initialStart={campaignInput.execution_start_date}
                                            initialEnd={campaignInput.execution_end_date}
                                            onClose={() => setShowRangePicker(false)} 
                                            onRangeSelect={handlePayloadDateSelection} 
                                        />
                                    )}
                                </div>

                                <div className="relative">
                                    <label className={`text-xs font-bold uppercase tracking-wider mb-2 block ${isDateRangeInvalid ? 'text-red-300' : 'text-slate-300'}`}>Duration (Days)</label>
                                    <div className="relative">
                                        <input 
                                            type="number" 
                                            name="days_until_election" 
                                            value={campaignInput.days_until_election} 
                                            onChange={handleInputChange} 
                                            className={`w-full bg-slate-950/50 border border-slate-700/60 rounded-lg px-4 py-2.5 text-sm text-white pr-10 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all ${isDateRangeInvalid ? 'border-red-500 bg-red-900/20 text-red-100' : ''}`} 
                                        />
                                        <button
                                            onClick={() => setShowDurationCalendar(!showDurationCalendar)}
                                            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded transition-colors ${
                                                showDurationCalendar 
                                                ? "bg-white text-slate-900" 
                                                : isDateRangeInvalid 
                                                    ? "text-red-400 hover:text-white hover:bg-red-700"
                                                    : "text-slate-400 hover:text-white hover:bg-slate-700"
                                            }`}
                                        >
                                            <Calendar size={14} />
                                        </button>
                                    </div>
                                    {showDurationCalendar && (
                                        <MiniCalendar 
                                            onClose={() => setShowDurationCalendar(false)} 
                                            onDateSelect={handleDurationDateSelection} 
                                        />
                                    )}
                                </div>
                            </div>
                            {isDateRangeInvalid && (
                                <div className="flex items-center gap-2 mt-2 text-red-400 text-xs font-semibold animate-in fade-in slide-in-from-top-1">
                                    <AlertCircle size={14} />
                                    <span>Target dates extend beyond the election countdown</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* CHANNEL SELECTION */}
                <div>
                    <div className="flex items-center justify-between mb-6 relative">
                        <div className="flex items-center space-x-3">
                            <Activity className="text-white" size={20} />
                            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">Channel Selection</h3>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={handleGlobalSelection}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-wide transition-colors ${
                                    isMultiSelection 
                                    ? "bg-red-900/30 text-red-300 hover:bg-red-900/50 hover:text-red-200 border border-red-800/50" 
                                    : "bg-slate-950/30 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-700"
                                }`}
                            >
                                {isMultiSelection ? <><X size={14} /> Clear</> : <><CheckSquare size={14} /> Select All</>}
                            </button>

                            <div className="relative">
                                <button 
                                    onClick={() => setShowCalculator(!showCalculator)}
                                    className={`p-1.5 rounded transition-colors ${
                                        showCalculator 
                                        ? "bg-white text-slate-900" 
                                        : "bg-slate-950/30 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-700"
                                    }`}
                                >
                                    <Calculator size={18} />
                                </button>
                                {showCalculator && <MiniCalculator onClose={() => setShowCalculator(false)} />}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* SECTION 1: FIELD OPERATIONS */}
                        <div className="bg-slate-900/20 rounded-xl p-4 border border-slate-800/60">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-1.5 rounded bg-blue-900/30 text-blue-400 border border-blue-500/20">
                                    <Users size={16} />
                                </div>
                                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Field Operations & Volunteers</h4>
                                <span className="text-[10px] text-slate-400 font-mono ml-auto bg-slate-950/50 px-2 py-0.5 rounded-full border border-slate-800">Requires Time & Effort</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {fieldOpsChannels.map(channel => (
                                    <ChannelCard 
                                        key={channel.channel} 
                                        channel={channel} 
                                        type="field" 
                                        campaignInput={campaignInput}
                                        onToggle={handleChannelToggle}
                                        onCostChange={handleChannelCostChange}
                                        onHoursChange={handleChannelHoursChange}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* SECTION 2: PAID MEDIA */}
                        <div className="bg-slate-900/20 rounded-xl p-4 border border-slate-800/60">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-1.5 rounded bg-emerald-900/30 text-emerald-400 border border-emerald-500/20">
                                    <Megaphone size={16} />
                                </div>
                                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Paid Media & Direct Outreach</h4>
                                <span className="text-[10px] text-slate-400 font-mono ml-auto bg-slate-950/50 px-2 py-0.5 rounded-full border border-slate-800">Requires Budget</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {paidMediaChannels.map(channel => (
                                    <ChannelCard 
                                        key={channel.channel} 
                                        channel={channel} 
                                        type="media"
                                        campaignInput={campaignInput}
                                        onToggle={handleChannelToggle}
                                        onCostChange={handleChannelCostChange}
                                        onHoursChange={handleChannelHoursChange}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* SWING SEAT SUGGESTIONS (FIXED AESTHETIC) */}
                <div>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                        <div className="flex items-start space-x-3">
                            <BarChart3 className="text-white flex-shrink-0" size={20} />
                            <div className="flex flex-col">
                                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Swing Seat Suggestions</h3>
                                <p className="text-[10px] font-medium text-slate-500 mt-0.5">High-Impact Target Wards</p>
                            </div>
                        </div>

                        {swingSeats && swingSeats.length > 0 && (
                            <div className="flex items-center gap-2 text-xs">
                                <button 
                                    onClick={() => setSwingSort(prev => prev === 'desc' ? 'asc' : 'desc')}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                                >
                                    {swingSort === 'desc' ? (
                                        <>High to Low <ArrowDownWideNarrow size={14} /></>
                                    ) : (
                                        <>Low to High <ArrowUpWideNarrow size={14} /></>
                                    )}
                                </button>

                                <button 
                                    onClick={() => setFilterStrategy(prev => !prev)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded border transition-colors ${
                                        filterStrategy 
                                        ? "bg-slate-800 text-white border-white hover:bg-slate-700" 
                                        : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"
                                    }`}
                                >
                                    {filterStrategy ? <Filter size={14} /> : <ListFilter size={14} />}
                                    {filterStrategy ? "Strategy Matched" : "All Suggestions"}
                                </button>
                            </div>
                        )}
                    </div>

                    {processedSwingSeats && processedSwingSeats.length > 0 ? (
                        /* FIX: Changed 'pb-4 -mb-4' to 'py-6 px-2' 
                           This adds vertical padding inside the scroll container, preventing 
                           the scale transformation and shadow from being clipped by the overflow.
                        */
                        <div className="flex space-x-4 overflow-x-auto py-6 px-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
                            {processedSwingSeats.map(suggestion => (
                                <button
                                    key={suggestion.id}
                                    onClick={() => handleWardToggle(suggestion.id)}
                                    // UPDATED: Dark Glass with White Glow for Selected State
                                    className={`flex-shrink-0 w-64 p-4 rounded-lg border-2 text-left transition-all duration-300 space-y-2 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                                        campaignInput.selected_ward_ids.includes(suggestion.id)
                                            ? 'bg-slate-900/90 border-white shadow-[0_0_20px_rgba(255,255,255,0.25)] text-white scale-[1.02]' 
                                            : `${getWinabilityColorClass(suggestion.winability?.score || 0)} opacity-90 hover:opacity-100`
                                    }`}
                                >
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-bold truncate text-white">{suggestion.ward}</h4>
                                        {campaignInput.selected_ward_ids.includes(suggestion.id) && <CheckCircle size={16} className="text-white flex-shrink-0" />}
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-400">Winability Score</span>
                                        <p className="text-lg font-semibold text-slate-100">{parseFloat((suggestion.winability?.score || 0) * 100).toFixed(2)}%</p>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-400">Strategy</span>
                                        <p className="text-sm text-slate-200 capitalize truncate">
                                            {suggestion.strategic_priority?.campaign_strategy?.replace(/_/g, ' ').toLowerCase() || "N/A"}
                                        </p>
                                    </div>
                                    <div className="text-xs text-slate-400 pt-2 border-t border-slate-700/50">
                                        Margin: <span className="font-mono text-slate-300">
                                            {Math.round(suggestion.swing_analysis?.current_margin_pct || 0) > 0 
                                            ? `+${Math.round(suggestion.swing_analysis?.current_margin_pct || 0)}` 
                                            : Math.round(suggestion.swing_analysis?.current_margin_pct || 0)}%
                                        </span> 
                                        | Swing: <span className="font-mono text-slate-300">
                                            {Math.round(suggestion.swing_analysis?.swing_required_pct || 0)}%
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-24 rounded-lg bg-slate-950/30 text-slate-500 border border-slate-800/50">
                            <p>
                                {filterStrategy 
                                    ? `No wards align with the "${focusLabel}" strategy.` 
                                    : "No swing seat suggestions available."}
                            </p>
                        </div>
                    )}
                </div>

                {/* Ward Selection & Action Bar */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <MapPin className="text-white" size={20} />
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Target Wards</h3>
                        </div>
                        {campaignInput.selected_ward_ids.length > 0 && (
                            <button 
                                onClick={handleClearWards}
                                className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold text-slate-400 hover:text-white hover:bg-red-900/30 transition-colors"
                            >
                                <Trash2 size={12} /> Clear All
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto custom-scrollbar pr-1 bg-slate-950/30 p-4 rounded-lg border border-slate-800/50">
                        {allWards.map(ward => (
                            <button 
                                key={ward.id} 
                                onClick={() => handleWardToggle(ward.id)} 
                                // UPDATED: Subtle Indigo/White Glow for Selected Wards
                                className={`py-2 px-3 rounded-md text-xs transition-all duration-200 border text-left truncate ${
                                    campaignInput.selected_ward_ids.includes(ward.id) 
                                    ? 'bg-indigo-500/20 text-white border-indigo-400/50 shadow-[0_0_10px_rgba(99,102,241,0.25)] font-medium' 
                                    : 'text-slate-400 bg-slate-900/40 hover:bg-slate-800/60 border-transparent hover:text-slate-200'
                                }`}
                            >
                                {campaignInput.selected_ward_ids.includes(ward.id) && <CheckCircle size={14} className="inline-block mr-1 text-indigo-400" />}
                                {ward.name}
                            </button>
                        ))}
                    </div>
                    <div className="mt-4 text-xs text-slate-500">
                        {campaignInput.selected_ward_ids.length === 0 ? (
                            <span>Targeting Scope: <span className="text-white font-bold">Campaign Wide (All Wards)</span></span>
                        ) : (
                            <span>Selected Wards: <span className="text-white font-bold">{campaignInput.selected_ward_ids.length}</span></span>
                        )}
                    </div>
                </div>

                {/* Footer Action Bar */}
                <div className="px-6 py-4 bg-slate-950/30 border-t border-slate-800/50 flex justify-between items-center -mx-6 -mb-6 rounded-b-xl">
                    <div className="text-xs text-slate-400">
                        <p>Ready to simulate for <strong className="text-white">{focusLabel}</strong></p>
                        <p>Across <strong className="text-white">{effectiveWardCount} {effectiveWardCount === 1 ? 'ward' : 'wards'}</strong> using <strong className="text-white">{channelCount} channels</strong> with a <strong className="text-white">{formattedBudget}</strong> budget.</p>
                    </div>
                    <button 
                        onClick={handleRunOptimization} 
                        className={`
                            flex items-center gap-2 px-6 py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all duration-300 shadow-lg
                            ${isDateRangeInvalid || isLoading || allWards.length === 0 || channelCount === 0
                                ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                                : 'bg-white text-slate-900 hover:bg-slate-200 border border-white shadow-[0_0_15px_rgba(255,255,255,0.2)] hover:shadow-[0_0_25px_rgba(255,255,255,0.4)]'
                            }
                        `}
                        disabled={isLoading || allWards.length === 0 || channelCount === 0 || isDateRangeInvalid}
                        title={isDateRangeInvalid ? "Fix date range error to proceed" : "Run Simulation"}
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.91l3-2.619z"></path>
                                </svg>
                                <span>Running...</span>
                            </>
                        ) : (
                            <span>Run Simulation</span>
                        )}
                    </button>
                </div>
            </GlassContainer>

            {/* 2. Right Panel: Results (Dark Glass Reskin) */}
            <GlassContainer className="p-6 min-h-[400px]">
                {!results && !isLoading && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500">
                        <BarChart3 size={48} className="text-slate-600 mb-4" />
                        <h2 className="text-sm font-bold text-white uppercase tracking-widest mb-2">Analysis Results</h2>
                        <p className="text-xs text-slate-500 text-center max-w-xs">Configure your campaign and click "Run Simulation" to see the results here.</p>
                    </div>
                )}
                
                {isLoading && (
                 <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-6">
                    <div className="w-12 h-12">
                    <span className="loader block"></span>
                    </div>
            
                    <div className="text-center">
                        <h2 className="text-sm font-bold text-white uppercase tracking-widest mb-2">Analysing Campaign...</h2>
                        <p className="text-xs text-slate-500">Please wait whilst we render the matrix.</p>
                    </div>
                 </div>
                )}
                
                {results && <ResultsDisplay results={results} campaignInput={campaignInput} openChannel={openChannel} setOpenChannel={setOpenChannel} campaignId={campaignId} setActiveView={setActiveView} />}
            </GlassContainer>
        </div>
    );
};